<template>
  <div class="org-dependency">
    <!-- 机构检索 -->
    <div class="section-card org-search-card">
      <div class="org-search-head">
        <div class="card-title">
          <span class="title-main">机构解析链检索</span>
          <span class="subtitle">输入自然语言机构名或 AS 号，匹配 ASN 后展示该机构开放解析器的上下游依赖</span>
        </div>
        <div class="org-search-form">
          <el-input
            v-model="orgInput"
            size="small"
            clearable
            class="org-search-input"
            placeholder="例如：清华大学 / CERNET / China Telecom / AS4134"
            @input="onOrgInputChange"
            @keyup.enter.native="runOrgSearch"
            @clear="handleOrgInputClear"
          ></el-input>
          <el-button
            type="primary"
            size="small"
            icon="el-icon-search"
            :loading="searchPending"
            @click="runOrgSearch"
          >查询</el-button>
        </div>
      </div>

      <!-- 索引元信息：原来挂在页面顶部的时间条，嵌入后收进本板块 -->
      <div class="org-index-meta">
        索引生成时间：{{ indexGeneratedAt }}
        <span class="dot">·</span> 覆盖 ASN <span class="highlight">{{ formatNumber(indexAsnCount) }}</span>
      </div>

      <!-- 匹配到的机构候选 -->
      <div class="org-match-list" v-if="matchedOrgs.length || orgInput">
        <button
          type="button"
          class="org-chip"
          :class="{ active: selected && selected.asn === item.asn }"
          v-for="item in matchedOrgs"
          :key="item.asn"
          @click="selectAsn(item.asn)"
        >
          <span class="chip-asn">AS{{ item.asn }}</span>
          <span class="chip-org">{{ item.org }}</span>
          <span class="chip-meta">{{ displayCountryCode(item.country) }} · {{ formatNumber(item.resolver_count) }}</span>
        </button>
        <div class="empty-tip" v-if="!matchedOrgs.length && orgInput && !indexLoading">
          没有匹配到机构或 ASN，试试更短的关键词
        </div>
      </div>
    </div>

    <!-- 指标卡 -->
    <div class="metric-cards">
      <div class="section-card metric-box" v-for="item in metricCards" :key="item.label">
        <div class="metric-label">{{ item.label }}</div>
        <div class="metric-value" :class="item.tone">{{ item.value }}</div>
        <div class="metric-hint">{{ item.hint }}</div>
      </div>
    </div>

    <div class="org-grid">
        <!-- 上下游拓扑 -->
        <div class="section-card org-graph-card">
          <div class="card-title">
            <span class="title-main">{{ graphTitle }}</span>
            <span class="subtitle">{{ graphSub }}</span>
          </div>
          <div class="graph-legend">
            <span><i class="dot dot-up"></i>上游</span>
            <span><i class="dot dot-down"></i>下游</span>
            <span><i class="dot dot-cve"></i>下游含潜在CVE</span>
            <span><i class="dot dot-single"></i>集中依赖边</span>
            <span><i class="dot dot-third"></i>第三方管辖边</span>
          </div>
          <div class="chart-container graph-chart" ref="graphContainer" v-loading="detailLoading"></div>
          <div class="node-detail" v-html="nodeDetailHtml"></div>
        </div>

        <!-- 解析器列表 -->
        <div class="section-card org-table-card">
          <div class="card-title">
            <span class="title-main">开放解析器列表</span>
            <span class="subtitle">{{ tableSub }}</span>
          </div>
          <div class="org-table-wrap">
            <el-table
              :data="tablePageRows"
              :row-class-name="tableRowClass"
              :height="tableBodyHeight"
              style="width: 100%"
              class="dark-table org-table"
              @row-click="handleTableRowClick"
            >
              <el-table-column prop="ip" label="IP" min-width="125"></el-table-column>
              <el-table-column prop="role" label="上游/下游" min-width="80" align="center">
                <template slot-scope="scope">
                  <span class="role-tag" :class="scope.row.roleClass">{{ scope.row.role }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="countryLabel" label="国家" min-width="80"></el-table-column>
              <el-table-column prop="org" label="组织" min-width="170" show-overflow-tooltip></el-table-column>
              <el-table-column prop="softwareLabel" label="软件版本" min-width="120" show-overflow-tooltip></el-table-column>
              <el-table-column prop="cveCount" label="CVE个数" min-width="85" align="right"></el-table-column>
              <el-table-column prop="connectionCount" label="连接数量" min-width="90" align="right"></el-table-column>
            </el-table>
          </div>
          <el-pagination
            v-if="tableTotal"
            layout="total, sizes, prev, pager, next"
            :current-page="tablePage"
            :page-size="tablePageSize"
            :page-sizes="tablePageSizes"
            :total="tableTotal"
            @current-change="handleTablePageChange"
            @size-change="handleTableSizeChange"
            class="dark-pagination"
          />
        </div>
    </div>
  </div>
</template>

<script>
import * as echarts from "echarts";

import {
  getOrgDependencyIndex,
  getOrgDependencyDetail
} from "@/service/dashboard";

// 与 DNS解析器演化追踪页保持同一套数据色，两张页面并排看不会串色
const DATA_BLUE = "#3987E5";
const RISK_RED = "#E66767";
const NODE_UPSTREAM = "#A855F7";

// 大机构（如 AS4134）会有上千节点，力导向布局超过这个规模浏览器扛不住，
// 按连接权重/CVE/结构风险排序后抽样，并明确标注是代表性拓扑
const GRAPH_NODE_LIMIT = 2600;
const GRAPH_LINK_LIMIT = 6200;

// 列表与左侧拓扑等高，单页条数控制在刚好铺满一屏，避免整页被表格拉到几千像素
// 默认 20 条：大机构明细上千行，首屏给最小粒度更快看到结果
const TABLE_PAGE_SIZE = 20;
const TABLE_PAGE_SIZES = [20, 50, 100];

const escapeHtml = (text) => String(text === undefined || text === null ? "" : text)
  .replace(/[&<>"']/g, m => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    "\"": "&quot;",
    "'": "&#39;"
  }[m]));

// 机构名归一化：去掉 “Co., Ltd.” 之类的后缀与标点，让 “China Telecom Group” 与 “china telecom” 能对齐
const ORG_SUFFIX = /\b(inc|llc|ltd|limited|co|corp|corporation|company|group|sa|bv|plc)\b/g;

export default {
  name: "OrgDependency",
  props: {
    // 父页面用 v-show 切换页签，容器在隐藏时尺寸为 0，需要显式通知重算图表尺寸
    active: {
      type: Boolean,
      default: true
    },
    // 父页面（基本态势顶部检索框 / 参考搜索按钮）带入的关键词，挂载后自动执行一次查询
    initialQuery: {
      type: String,
      default: ""
    },
    // 父页面恢复上次检索时带回的机构 ASN，优先于关键词匹配结果，直接定位该机构
    initialAsn: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      // 索引
      indexLoading: false,
      indexGeneratedAt: "--",
      indexAsnCount: 0,

      // 检索
      orgInput: "",
      matchedOrgs: [],
      selected: null,
      // 查询按钮转圈：只在“点查询到明细渲染完”这段为真，
      // 不再复用 indexLoading，否则索引加载完按钮先转一圈、真正查询时反而不转
      searchPending: false,

      // 明细
      detailLoading: false,
      detail: null,

      // 拓扑
      graphTitle: "上下游解析器节点拓扑结构图",
      graphSub: "等待选择机构",
      selectedNode: null,
      nodeDetailHtml: "点击拓扑中的节点后，这里会显示 IP、ASN、国家、机构、软件版本、CVE、连接数量和结构风险。",

      // 列表
      tablePage: 1,
      tablePageSize: TABLE_PAGE_SIZE,
      tablePageSizes: TABLE_PAGE_SIZES,
      tableRows: [],
      searchTimer: null,
      searchToken: 0
    };
  },
  computed: {
    metricCards() {
      const s = (this.detail && this.detail.summary) || {};

      return [
        {
          label: "上游解析器数量",
          value: this.detail ? this.formatNumber(s.upstream_count) : "-",
          hint: "NS-chain 观测到的 upstream IP",
          tone: "tone-up"
        },
        {
          label: "下游解析器数量",
          value: this.detail ? this.formatNumber(s.downstream_count) : "-",
          hint: "该 ASN 下开放解析器 target IP",
          tone: "tone-down"
        },
        {
          label: "集中依赖比例",
          value: this.detail ? this.formatShare(s.concentrated_dependency_pct) : "-",
          hint: "命中集中单点故障图的下游占比",
          tone: "tone-risk"
        },
        {
          label: "第三方管辖比例",
          value: this.detail ? this.formatShare(s.third_party_jurisdiction_pct) : "-",
          hint: "跨国家或跨组织依赖下游占比",
          tone: "tone-risk"
        },
        {
          label: "CVE潜在影响IP个数",
          value: this.detail ? this.formatNumber(s.cve_potential_ip_count) : "-",
          hint: "风险查询面板中的潜在 CVE IP",
          tone: "tone-cve"
        }
      ];
    },
    tableTotal() {
      return this.tableRows.length;
    },
    tablePageRows() {
      const start = (this.tablePage - 1) * this.tablePageSize;

      return this.tableRows.slice(start, start + this.tablePageSize);
    },
    // 表格高度跟随单页条数，页头 40px + 每行 34px + 分页条，避免固定高度把末行截掉
    tableBodyHeight() {
      return 40 + Math.max(1, Math.min(this.tablePageSize, this.tablePageRows.length || 1)) * 34 + 8;
    },
    tableSub() {
      if (!this.tableRows.length) {
        return "按连接数量从大到小排序";
      }
      const start = (this.tablePage - 1) * this.tablePageSize;

      return `按连接数量排序；当前显示 ${this.formatNumber(start + 1)}-${this.formatNumber(Math.min(this.tableRows.length, start + this.tablePageRows.length))} / 共 ${this.formatNumber(this.tableRows.length)} 条`;
    }
  },
  created() {
    // 明细里可能有上千节点，不放进 data 之外的响应式路径会省掉一次深遍历；
    // 拓扑数据只在渲染时读取，这里保持普通字段即可
    this.charts = {};
    this.index = null;
    this.graphData = null;
    // 父页面本次带入的关键词是否已执行过，避免索引就绪后重复检索
    this.appliedQuery = "";
    // 同上，针对父页面带入的 ASN
    this.appliedAsn = "";
  },
  mounted() {
    this.loadIndex();
    window.addEventListener("resize", this.handleResize);
  },
  watch: {
    // 页签切回来时容器才有真实尺寸，此时必须 resize，否则拓扑图是空白
    active(visible) {
      if (!visible) {
        return;
      }
      this.$nextTick(() => {
        window.requestAnimationFrame(() => this.handleResize());
      });
    },
    // 父页面检索框提交后带词进来（组件可能已挂载，也可能刚挂载）
    initialQuery(value) {
      if (value) {
        this.applyInitialQuery(value);
      }
    }
  },
  beforeDestroy() {
    Object.keys(this.charts).forEach(key => {
      if (this.charts[key]) {
        this.charts[key].dispose();
      }
    });
    this.charts = {};
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
    window.removeEventListener("resize", this.handleResize);
  },
  methods: {
    formatNumber(value) {
      const num = Number(value || 0);

      return Number.isFinite(num) ? num.toLocaleString("en-US") : "0";
    },

    formatShare(value) {
      return `${(Number(value) || 0).toFixed(2)}%`;
    },

    // TW / HK 在递归侧数据里按 CN_TW / CN_HK 口径展示，与演化追踪页保持一致
    displayCountryCode(code) {
      const value = String(code || "XX");

      if (value === "TW") {
        return "CN_TW";
      }
      if (value === "HK") {
        return "CN_HK";
      }
      return value;
    },

    displayCountryName(name, code) {
      const rawName = String(name || "Unknown");
      const raw = `${String(code || "")} ${rawName}`;

      if (code === "TW" || code === "CN_TW" || /Taiwan|台湾/i.test(raw)) {
        return "中国台湾";
      }
      if (code === "HK" || code === "CN_HK" || /Hong Kong|香港/i.test(raw)) {
        return "中国香港";
      }
      return rawName;
    },

    normalizeOrgText(value) {
      return String(value === undefined || value === null ? "" : value)
        .toLowerCase()
        .replace(/[^a-z0-9一-鿿]+/g, " ")
        .replace(ORG_SUFFIX, " ")
        .replace(/\s+/g, " ")
        .trim();
    },

    // 索引 4.3MB / 10115 条，首屏按需加载一次后常驻
    async loadIndex() {
      this.indexLoading = true;
      try {
        const data = await getOrgDependencyIndex();

        this.index = data;
        this.indexAsnCount = (data.meta && data.meta.asn_count) || (data.orgs || []).length;
        this.indexGeneratedAt = (data.meta && data.meta.generated_at_utc) || "--";
        // 未输入时先给样本量最大的几个机构，避免空白
        this.matchedOrgs = this.topOrgs(8);
      } catch (error) {
        console.error("[OrgDependency] loadIndex 失败:", error);
        this.$message.error("机构索引加载失败");
      } finally {
        // 必须先清 loading 再补检索：runOrgSearch 会挡住 loading 中的调用，
        // 早一步执行只会弹“索引仍在加载”，关键词就白带了
        this.indexLoading = false;
      }
      // 刷新恢复：父页面带了确切 ASN，直接定位该机构，
      // 跳过关键词匹配（打分可能把同一个词落到别的机构上）
      if (String(this.initialAsn || "").trim() && this.appliedAsn !== String(this.initialAsn).trim()) {
        this.orgInput = this.orgInput || String(this.initialQuery || "").trim();
        this.applyInitialAsn(this.initialAsn);
        return;
      }
      // 父页面带词进来（无论挂载时已带还是索引就绪后才带）都补执行一次检索
      this.applyInitialQuery(this.initialQuery);
    },

    // 按 ASN 直接还原上次选中的机构；无 ASN 时不做任何事
    applyInitialAsn(asn) {
      const text = String(asn || "").trim();

      if (!text || !this.index) {
        return;
      }
      if (this.appliedAsn === text) {
        return;
      }
      this.appliedAsn = text;
      const item = (this.index.orgs || []).find(x => String(x.asn) === text);

      if (!item) {
        return;
      }
      this.orgInput = this.orgInput || item.org || `AS${text}`;
      this.matchedOrgs = this.findMatches(this.orgInput);
      this.selectAsn(text);
    },

    // 把父页面带入的关键词写进检索框并触发查询；索引未就绪时先记下，加载完成后补执行
    applyInitialQuery(query) {
      const text = String(query || "").trim();

      if (!text) {
        return;
      }
      this.orgInput = text;
      if (!this.index) {
        return;
      }
      if (this.appliedQuery === text) {
        return;
      }
      this.appliedQuery = text;
      this.runOrgSearch();
    },

    topOrgs(count) {
      if (!this.index) {
        return [];
      }
      return (this.index.orgs || [])
        .slice()
        .sort((a, b) => Number(b.resolver_count || 0) - Number(a.resolver_count || 0))
        .slice(0, count);
    },

    onOrgInputChange() {
      if (!this.index) {
        return;
      }
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }
      this.searchTimer = setTimeout(() => {
        // 索引常驻内存，匹配是纯计算，防抖只是为了避免连续输入时反复排序上万条
        this.matchedOrgs = this.findMatches(this.orgInput);
      }, 200);
    },

    handleOrgInputClear() {
      this.matchedOrgs = this.topOrgs(8);
    },

    // 评分口径沿用上游 demo：AS 号精确命中 > 全串相等 > 包含 > 分词命中
    scoreOrg(item, query) {
      const q = this.normalizeOrgText(query);

      if (!q) {
        return 0;
      }
      const asnQuery = q.match(/^as\s*(\d+)$/) || q.match(/^(\d+)$/);

      if (asnQuery && String(item.asn) === asnQuery[1]) {
        return 10000;
      }
      const hay = item.search_text
        || this.normalizeOrgText([item.asn, item.org, ...(item.aliases || [])].join(" "));

      if (hay === q) {
        return 9000;
      }
      if (hay.includes(q)) {
        return 7000 + Math.min(500, q.length * 10);
      }
      const tokens = q.split(" ").filter(Boolean);

      let hit = 0;

      tokens.forEach(token => {
        if (hay.includes(token)) {
          hit += 1;
        }
      });
      return hit ? hit * 100 + Math.min(80, Number(item.resolver_count || 0) / 100) : 0;
    },

    findMatches(query) {
      const list = (this.index && this.index.orgs) || [];

      if (!String(query || "").trim()) {
        return this.topOrgs(8);
      }
      return list
        .map(item => ({ item, score: this.scoreOrg(item, query) }))
        .filter(entry => entry.score > 0)
        .sort((a, b) => b.score - a.score || Number(b.item.resolver_count || 0) - Number(a.item.resolver_count || 0))
        .slice(0, 12)
        .map(entry => entry.item);
    },

    async runOrgSearch() {
      const query = this.orgInput.trim();

      if (!query) {
        this.$message.warning("请输入机构名或 AS 号");
        return;
      }
      if (this.indexLoading) {
        this.$message.warning("机构索引仍在加载，请稍候");
        return;
      }
      if (!this.index) {
        this.$message.warning("机构索引未就绪");
        return;
      }
      const matches = this.findMatches(query);

      this.matchedOrgs = matches;
      if (!matches.length) {
        this.$message.warning("没有匹配到机构或 ASN");
        return;
      }
      // 匹配是纯本地计算，真正的等待在明细请求上；覆盖整段便于网络慢时给出反馈
      this.searchPending = true;
      try {
        await this.selectAsn(matches[0].asn);
      } finally {
        this.searchPending = false;
      }
    },

    async selectAsn(asn) {
      const item = ((this.index && this.index.orgs) || []).find(x => String(x.asn) === String(asn));

      if (!item) {
        return;
      }
      this.selected = item;
      // 通知父页面记住当前 ASN（刷新恢复用）；关键词可能匹配多个机构，只存这个才精确
      this.$emit("select", { asn: item.asn, org: item.org });
      this.detailLoading = true;
      const token = ++this.searchToken;

      try {
        const detail = await getOrgDependencyDetail(item.asn);

        if (token !== this.searchToken) {
          return;
        }
        this.detail = detail;
        this.selectedNode = null;
        this.tablePage = 1;
        this.buildTableRows(detail);
        this.nodeDetailHtml = "点击拓扑中的节点后，这里会显示 IP、ASN、国家、机构、软件版本、CVE、连接数量和结构风险。";
        // 图表容器在隐藏页签下尺寸为 0，首次选中后再初始化，避免 0 尺寸
        await this.$nextTick();
        // 若切页签时图表已存在但仍未渲染过，补一次 resize
        if (this.charts.graph && this.active) {
          window.requestAnimationFrame(() => this.renderGraph(detail));
        } else {
          this.renderGraph(detail);
        }
      } catch (error) {
        console.error("[OrgDependency] selectAsn 失败:", error);
        if (token === this.searchToken) {
          this.$message.error(`加载 AS${item.asn} 明细失败`);
          this.detail = null;
          this.graphSub = "明细加载失败";
        }
      } finally {
        if (token === this.searchToken) {
          this.detailLoading = false;
        }
      }
    },

    buildTableRows(detail) {
      const nodes = (detail && detail.nodes) || [];

      this.tableRows = nodes
        .slice()
        .sort((a, b) => Number(b.connection_count || 0) - Number(a.connection_count || 0)
          || String(a.ip).localeCompare(String(b.ip)))
        .map(node => ({
          ...node,
          roleClass: this.roleClass(node),
          countryLabel: this.displayCountryCode(node.country),
          softwareLabel: node.software_version || "-",
          cveCount: this.formatNumber(node.cve_count),
          connectionCount: this.formatNumber(node.connection_count)
        }));
    },

    roleClass(node) {
      if (String(node.role || "").includes("下游") && Number(node.cve_count || 0) > 0) {
        return "role-down-cve";
      }
      if (String(node.role || "").includes("下游")) {
        return "role-down";
      }
      return "role-up";
    },

    tableRowClass({ row }) {
      return this.selectedNode && this.selectedNode.ip === row.ip ? "row-selected" : "";
    },

    handleTableRowClick(row) {
      this.showNodeDetail(row);
      this.highlightSelectedNode();
    },

    // 边是否属于“集中依赖”：上游 demo 优先看显式标记，其次回落到源节点的集中单点标记
    hasRiskType(link, name) {
      const raw = [link.risk_type, link.riskType, ...(Array.isArray(link.risk_types) ? link.risk_types : [])]
        .filter(Boolean)
        .join("、");

      return raw.split(/[、,;\s]+/).includes(name);
    },

    isConcentratedLink(link, nodeByIp) {
      if (link.concentrated_dependency || this.hasRiskType(link, "集中依赖")) {
        return true;
      }
      const source = nodeByIp.get(link.source);

      return Boolean(source && String(source.role || "").includes("下游") && source.has_single_point);
    },

    isThirdPartyLink(link, nodeByIp) {
      if (link.third_party_jurisdiction || this.hasRiskType(link, "第三方管辖")) {
        return true;
      }
      const source = nodeByIp.get(link.source);
      const target = nodeByIp.get(link.target);

      if (!source || !target || !String(source.role || "").includes("下游")) {
        return false;
      }
      const sourceCountry = String(source.country || "XX");
      const targetCountry = String(target.country || "XX");

      if (sourceCountry !== "XX" && targetCountry !== "XX" && sourceCountry !== targetCountry) {
        return true;
      }
      const sourceOrg = this.normalizeOrgText(source.org || "");
      const targetOrg = this.normalizeOrgText(target.org || "");

      return Boolean(source.has_third_party && sourceOrg && targetOrg && sourceOrg !== targetOrg);
    },

    nodeColor(node) {
      if (String(node.role || "").includes("下游") && Number(node.cve_count || 0) > 0) {
        return RISK_RED;
      }
      if (String(node.role || "").includes("下游")) {
        return DATA_BLUE;
      }
      return NODE_UPSTREAM;
    },

    buildGraphData(detail) {
      const allNodes = (detail && detail.nodes) || [];
      const allLinks = (detail && detail.links) || [];
      const nodeByIp = new Map(allNodes.map(node => [node.ip, node]));
      const degree = new Map();

      allLinks.forEach(link => {
        const weight = Number(link.weight || 1);

        degree.set(link.source, (degree.get(link.source) || 0) + weight);
        degree.set(link.target, (degree.get(link.target) || 0) + weight);
      });

      // 抽样优先级：含 CVE > 有结构风险 > 上游 > 连接度，保证代表性节点一定进图
      const scored = allNodes.map(node => {
        const d = degree.get(node.ip) || Number(node.connection_count || 0);
        const cveBoost = Number(node.cve_count || 0) > 0 ? 1000000 : 0;
        const riskBoost = node.has_single_point || node.has_third_party ? 120000 : 0;
        const upstreamBoost = String(node.role || "").includes("上游") ? 20000 : 0;

        return { node, score: cveBoost + riskBoost + upstreamBoost + d };
      }).sort((a, b) => b.score - a.score);

      const selected = new Set();
      const nodeLimit = allNodes.length <= GRAPH_NODE_LIMIT ? allNodes.length : GRAPH_NODE_LIMIT;

      scored.slice(0, nodeLimit).forEach(entry => selected.add(entry.node.ip));

      const candidateLinks = allLinks
        .filter(link => selected.has(link.source) && selected.has(link.target))
        .sort((a, b) => {
          const rank = link => (this.isConcentratedLink(link, nodeByIp) ? 1 : 0)
            + (this.isThirdPartyLink(link, nodeByIp) ? 1 : 0);

          return rank(b) - rank(a) || Number(b.weight || 1) - Number(a.weight || 1);
        })
        .slice(0, allLinks.length <= GRAPH_LINK_LIMIT ? allLinks.length : GRAPH_LINK_LIMIT);

      candidateLinks.forEach(link => {
        selected.add(link.source);
        selected.add(link.target);
      });

      const shownNodes = [...selected].map(ip => nodeByIp.get(ip)).filter(Boolean);
      const maxDegree = Math.max(1, ...shownNodes.map(node => degree.get(node.ip) || Number(node.connection_count || 0)));
      const showLabels = shownNodes.length <= 220;
      const labelTop = new Set(
        shownNodes
          .slice()
          .sort((a, b) => (degree.get(b.ip) || 0) - (degree.get(a.ip) || 0))
          .slice(0, shownNodes.length <= 900 ? 90 : 45)
          .map(node => node.ip)
      );

      const nodes = shownNodes.map(node => {
        const d = degree.get(node.ip) || Number(node.connection_count || 0);
        const isDownstream = String(node.role || "").includes("下游");
        const cveCount = Number(node.cve_count || 0);

        return {
          id: node.ip,
          name: node.ip,
          value: d,
          category: isDownstream ? (cveCount > 0 ? 2 : 1) : 0,
          raw: node,
          symbolSize: Math.max(7, Math.min(34, 7 + Math.sqrt(d / maxDegree) * 26 + Math.sqrt(cveCount) * 1.2)),
          labelBrief: node.ip,
          label: {
            show: isDownstream
              && (showLabels || labelTop.has(node.ip) || cveCount > 0 || node.has_single_point || node.has_third_party),
            formatter: params => params.data.labelBrief,
            color: "#DBE9FF",
            fontSize: 10,
            position: "right"
          },
          itemStyle: {
            color: this.nodeColor(node),
            borderColor: this.selectedNode && this.selectedNode.ip === node.ip ? "#FEF3C7" : "rgba(255,255,255,0.42)",
            borderWidth: this.selectedNode && this.selectedNode.ip === node.ip ? 3 : 1
          },
          emphasis: { focus: "adjacency" }
        };
      });

      const links = candidateLinks.map(link => {
        const concentrated = this.isConcentratedLink(link, nodeByIp);
        const thirdParty = this.isThirdPartyLink(link, nodeByIp);
        const risky = concentrated || thirdParty;
        const riskType = [concentrated ? "集中依赖" : "", thirdParty ? "第三方管辖" : ""].filter(Boolean).join("、");
        const weight = Number(link.weight || 1);
        const riskColor = concentrated && thirdParty
          ? "rgba(45,212,191,0.98)"
          : (thirdParty ? "rgba(45,212,191,0.95)" : "rgba(250,178,25,0.95)");

        return {
          source: link.source,
          target: link.target,
          value: weight,
          riskType,
          symbol: risky ? ["none", "arrow"] : ["none", "none"],
          symbolSize: risky ? [0, thirdParty ? 12 : 10] : [0, 0],
          lineStyle: {
            width: risky ? Math.max(2.6, Math.min(7.5, 2.6 + Math.log1p(weight))) : Math.max(0.28, Math.min(2.2, 0.28 + Math.log1p(weight) / 3)),
            opacity: risky ? 0.88 : 0.16,
            color: risky ? riskColor : "rgba(140,155,181,0.28)",
            curveness: risky ? 0.16 : 0.06,
            shadowBlur: thirdParty ? 14 : (concentrated ? 9 : 0),
            shadowColor: thirdParty ? "rgba(45,212,191,0.78)" : "rgba(250,178,25,0.52)"
          },
          emphasis: { lineStyle: { width: risky ? 8.5 : 3, opacity: 0.95 } },
          effect: risky
            ? {
              show: true,
              period: thirdParty ? 2.6 : 3.2,
              trailLength: 0.34,
              color: thirdParty ? "#99F6E4" : "#FDE68A",
              symbol: "arrow",
              symbolSize: thirdParty ? 10 : 9
            }
            : { show: false }
        };
      });

      return {
        nodes,
        links,
        sampled: shownNodes.length < allNodes.length || candidateLinks.length < allLinks.length
      };
    },

    renderGraph(detail) {
      const chart = this.getChart("graph", "graphContainer");

      if (!chart) {
        return;
      }
      const graph = this.buildGraphData(detail);

      this.graphData = graph;
      const summary = (detail && detail.summary) || {};
      const orgLabel = detail && detail.org ? detail.org : "";

      this.graphTitle = `AS${detail.asn} · ${orgLabel}`;
      this.graphSub = `${this.formatNumber(summary.node_count)} 个节点，${this.formatNumber(summary.link_count)} 条连接；`
        + `当前绘制 ${this.formatNumber(graph.nodes.length)} 节点 / ${this.formatNumber(graph.links.length)} 边`
        + (graph.sampled ? "（代表性拓扑抽样）" : "（完整拓扑）");

      chart.setOption({
        backgroundColor: "transparent",
        color: [NODE_UPSTREAM, DATA_BLUE, RISK_RED],
        tooltip: {
          confine: true,
          backgroundColor: "rgba(8,20,38,0.96)",
          borderColor: "rgba(72,120,190,0.45)",
          textStyle: { color: "#EDF5FF", fontSize: 12 },
          formatter: params => {
            if (params.dataType !== "node") {
              const risk = params.data && params.data.riskType
                ? `<br/>风险类型：${escapeHtml(params.data.riskType)}`
                : "";

              return `连接权重：${this.formatNumber(params.value || 1)}${risk}`;
            }
            const node = (params.data && params.data.raw) || {};

            return [
              `<b>${escapeHtml(node.ip)}</b>`,
              `角色：${escapeHtml(node.role || "-")}`,
              `国家：${escapeHtml(this.displayCountryCode(node.country))} / ${escapeHtml(this.displayCountryName(node.country_name, node.country))}`,
              `机构：AS${escapeHtml(node.asn || "0")} · ${escapeHtml(node.org || "Unknown")}`,
              `软件版本：${escapeHtml(node.software_version || "未识别/无风险记录")}`,
              `CVE 个数：${this.formatNumber(node.cve_count)}`,
              `连接数量：${this.formatNumber(node.connection_count)}`
            ].join("<br/>");
          }
        },
        legend: {
          top: 6,
          right: 10,
          textStyle: { color: "rgba(255,255,255,0.65)", fontSize: 11 },
          data: ["上游", "下游", "下游含潜在CVE"]
        },
        series: [
          {
            type: "graph",
            name: "解析链拓扑",
            layout: "force",
            roam: true,
            draggable: true,
            categories: [
              { name: "上游", itemStyle: { color: NODE_UPSTREAM } },
              { name: "下游", itemStyle: { color: DATA_BLUE } },
              { name: "下游含潜在CVE", itemStyle: { color: RISK_RED } }
            ],
            data: graph.nodes,
            links: graph.links,
            edgeSymbol: ["none", "none"],
            edgeSymbolSize: [0, 0],
            force: {
              repulsion: graph.nodes.length > 1500 ? 48 : 120,
              gravity: graph.nodes.length > 1500 ? 0.085 : 0.045,
              edgeLength: graph.nodes.length > 1500 ? [18, 72] : [50, 150],
              friction: 0.62,
              layoutAnimation: true
            },
            lineStyle: { color: "rgba(140,155,181,0.34)", opacity: 0.18 },
            emphasis: { focus: "adjacency", lineStyle: { opacity: 0.75, width: 2.5 } },
            progressive: 800,
            progressiveThreshold: 1800,
            animationDuration: 500,
            animationEasingUpdate: "quinticInOut"
          }
        ]
      }, true);

      chart.off("click");
      chart.on("click", params => {
        if (params.dataType === "node" && params.data && params.data.raw) {
          this.showNodeDetail(params.data.raw);
          this.highlightSelectedNode();
        }
      });
    },

    getChart(key, refName) {
      if (!this.charts[key] && this.$refs[refName]) {
        this.charts[key] = echarts.init(this.$refs[refName], null, { renderer: "canvas" });
      }
      return this.charts[key];
    },

    // 选中态只改描边，全量 setOption 会打断力导向布局
    highlightSelectedNode() {
      const chart = this.charts.graph;

      if (!chart || !this.graphData) {
        return;
      }
      const selectedIp = this.selectedNode ? this.selectedNode.ip : "";

      chart.setOption({
        series: [{
          data: this.graphData.nodes.map(node => ({
            id: node.id,
            itemStyle: {
              borderColor: node.id === selectedIp ? "#FEF3C7" : "rgba(255,255,255,0.42)",
              borderWidth: node.id === selectedIp ? 3 : 1
            }
          }))
        }]
      });
    },

    showNodeDetail(node) {
      this.selectedNode = node;
      const cves = (node.cves || []).slice(0, 20).join("、") || "无";
      const risks = (node.risk_types || []).join("、") || [
        node.has_single_point ? "集中单点" : "",
        node.has_third_party ? "第三方管辖" : ""
      ].filter(Boolean).join("、") || "无明显结构风险";

      this.nodeDetailHtml = [
        `<span class="node-detail-pill">${escapeHtml(node.role || "-")}</span>`,
        `IP：<b>${escapeHtml(node.ip)}</b>`,
        `国家：${escapeHtml(this.displayCountryCode(node.country))} / ${escapeHtml(this.displayCountryName(node.country_name, node.country))}`,
        `ASN/机构：AS${escapeHtml(node.asn || "0")} · ${escapeHtml(node.org || "Unknown")}`,
        `网段：${escapeHtml(node.net24 || "-")}`,
        `软件版本：${escapeHtml(node.software_version || "未识别/无风险记录")}`,
        `CVE个数：${this.formatNumber(node.cve_count)}；CVE：${escapeHtml(cves)}`,
        `连接数量：${this.formatNumber(node.connection_count)}`,
        `结构风险：${escapeHtml(risks)}`
      ].join("<br/>");
    },

    handleTablePageChange(page) {
      this.tablePage = page;
    },

    handleTableSizeChange(size) {
      this.tablePageSize = size;
      this.tablePage = 1;
    },

    handleResize() {
      Object.keys(this.charts).forEach(key => {
        if (this.charts[key]) {
          this.charts[key].resize();
        }
      });
    }
  }
};
</script>

<style scoped lang="less">
// 作为组件嵌入 DNS解析器演化追踪页，本身不再承担页面级背景与滚动，
// 只负责内部板块的纵向排布
.org-dependency {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 15px;

  @media (min-width: 768px) {
    gap: 20px;
  }
}

// 索引元信息（原来是页面顶部的时间条）
.org-index-meta {
  margin-top: 10px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;

  .dot { margin: 0 6px; }

  .highlight {
    color: #40a9ff;
    font-weight: bold;
  }
}

.section-card {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  padding: clamp(12px, 1.6vw, 18px);
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;
}

.card-title {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 6px 12px;
  margin-bottom: 12px;

  .title-main {
    font-size: clamp(14px, 1.4vw, 17px);
    font-weight: bold;
    color: #fff;
  }

  .subtitle {
    color: rgba(255, 255, 255, 0.55);
    font-size: 12px;
  }
}

// ---------- 机构检索 ----------
.org-search-card {
  padding: clamp(10px, 1.3vw, 16px) clamp(12px, 1.6vw, 18px);
}

.org-search-head {
  display: flex;
  flex-direction: column;
  gap: 10px;

  .card-title { margin-bottom: 0; }

  @media (min-width: 1024px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
  }
}

.org-search-form {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 1 auto;

  .org-search-input { width: 380px; }

  @media (max-width: 767px) {
    width: 100%;

    .org-search-input { width: 100%; }
  }
}

.org-match-list {
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
  gap: 8px;
  margin-top: 12px;
  // 候选最多 12 条，给足 4 行的高度，避免出现内部滚动条把最后一行截断
  min-height: 42px;
  max-height: 168px;
  overflow-y: auto;
}

.org-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 5px 11px;
  border: 1px solid rgba(114, 149, 207, 0.28);
  border-radius: 999px;
  background: rgba(15, 34, 62, 0.72);
  color: #C1D5FF;
  font-size: 12px;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease, color 0.2s ease;

  .chip-asn {
    color: #40A9FF;
    font-weight: bold;
    font-variant-numeric: tabular-nums;
  }

  .chip-org {
    max-width: 300px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .chip-meta {
    color: rgba(255, 255, 255, 0.45);
    font-variant-numeric: tabular-nums;
  }

  &:hover {
    border-color: #7DD3FC;
    color: #fff;
  }

  &.active {
    border-color: #40A9FF;
    background: rgba(64, 169, 255, 0.22);
    color: #fff;

    .chip-asn { color: #9CCBFF; }
  }
}

// ---------- 指标卡 ----------
.metric-cards {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 15px;

  @media (min-width: 1024px) {
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 20px;
  }
}

.metric-box {
  min-height: 96px;
  justify-content: center;

  .metric-label {
    color: rgba(255, 255, 255, 0.55);
    font-size: 13px;
  }

  .metric-value {
    margin-top: 8px;
    font-size: clamp(20px, 2.2vw, 28px);
    font-weight: 800;
    color: #EDF5FF;
    font-variant-numeric: tabular-nums;

    &.tone-up { color: #C4A6FF; }
    &.tone-down { color: #6FB4F5; }
    &.tone-risk { color: RISK_AMBER; }
    &.tone-cve { color: RISK_RED; }
  }

  .metric-hint {
    margin-top: 5px;
    color: rgba(255, 255, 255, 0.35);
    font-size: 11px;
  }
}

// ---------- 拓扑 + 列表 ----------
.org-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 15px;
  min-width: 0;

  @media (min-width: 1280px) {
    grid-template-columns: minmax(0, 1.15fr) minmax(0, 0.85fr);
    gap: 20px;
  }
}

.graph-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
  margin-bottom: 8px;
  color: rgba(255, 255, 255, 0.55);
  font-size: 12px;

  .dot {
    display: inline-block;
    width: 9px;
    height: 9px;
    margin-right: 5px;
    border-radius: 50%;
    vertical-align: -1px;
  }

  .dot-up { background: NODE_UPSTREAM; }
  .dot-down { background: DATA_BLUE; }
  .dot-cve { background: RISK_RED; }
  .dot-single { background: RISK_AMBER; }
  .dot-third { background: RISK_TEAL; }
}

// 拓扑画布取正方形：力导向布局在等宽等高时展开最充分，
// 原来 640px 定高在宽屏下纵向偏扁，节点容易挤成一条带
.graph-chart {
  aspect-ratio: 1 / 1;
  height: auto;
  min-height: 460px;
  max-height: 92vh;
  width: 100%;
}

// 窄视口下拓扑卡片只剩单列、画布宽度不足，正方形会把高度压到看不清节点，
// 此时退回定高，宽度撑满即可（1280px 与 .org-grid 的双列断点保持一致）
@media (max-width: 1279px) {
  .graph-chart {
    aspect-ratio: auto;
    height: 640px;
  }
}

// 表格卡片总高与左侧拓扑卡片对齐：拓扑 640 + 图例 + 标题 + 节点详情
.org-table-card {
  min-height: 0;
}

.org-table-wrap {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

.node-detail {
  margin-top: 10px;
  padding: 11px 13px;
  border: 1px solid rgba(114, 149, 207, 0.2);
  border-radius: 6px;
  background: rgba(8, 20, 38, 0.5);
  color: rgba(255, 255, 255, 0.72);
  font-size: 12px;
  line-height: 1.8;
  min-height: 92px;

  /deep/ b { color: #fff; }

  /deep/ .node-detail-pill {
    display: inline-block;
    margin-right: 6px;
    padding: 1px 8px;
    border: 1px solid rgba(64, 169, 255, 0.5);
    border-radius: 999px;
    color: #7EC1FF;
    font-size: 11px;
  }
}

.org-table {
  /deep/ .row-selected td.el-table__cell {
    background: rgba(64, 169, 255, 0.16) !important;
  }

  .role-tag {
    display: inline-block;
    padding: 1px 8px;
    border-radius: 999px;
    font-size: 11px;

    &.role-up {
      background: rgba(168, 85, 247, 0.18);
      color: #C4A6FF;
    }

    &.role-down {
      background: rgba(57, 135, 229, 0.18);
      color: #7EC1FF;
    }

    &.role-down-cve {
      background: rgba(230, 103, 103, 0.2);
      color: #F0A0A0;
    }
  }
}

// ---------- 表格深色主题 ----------
// el-table 默认是白底，必须整体覆盖，否则和深蓝面板割裂
/deep/ .el-table {
  background: transparent;
  color: #fff;

  &::before,
  &::after { display: none; }

  th.el-table__cell {
    background: rgba(24, 144, 255, 0.1);
    color: #fff;
    font-weight: bold;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }

  tr,
  tr:hover > td.el-table__cell {
    background: transparent;
  }

  td.el-table__cell {
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    padding: 6px 0;
    color: rgba(255, 255, 255, 0.8);
  }

  .cell {
    padding-left: 8px;
    padding-right: 8px;
  }

  tbody tr:hover > td.el-table__cell {
    background: rgba(24, 144, 255, 0.12) !important;
  }

  // 固定表头在深色背景下的分隔线
  .el-table__fixed-header-wrapper th.el-table__cell,
  .el-table__header-wrapper th.el-table__cell {
    background: #0d2340;
  }

  .el-table__body-wrapper {
    &::-webkit-scrollbar {
      width: 6px;
      height: 6px;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.2);
      border-radius: 3px;
    }
  }

  // 空数据文案
  .el-table__empty-block {
    background: transparent;
  }

  .el-table__empty-text {
    color: rgba(255, 255, 255, 0.45);
  }
}

/deep/ .el-pagination {
  margin-top: 10px;
  padding: 0;
  color: rgba(255, 255, 255, 0.7);
  text-align: right;

  button,
  .el-pager li {
    background: rgba(24, 144, 255, 0.12);
    color: rgba(255, 255, 255, 0.75);
    border-radius: 3px;
    min-width: 28px;

    &:hover { color: #40A9FF; }

    &.active {
      background: #40A9FF;
      color: #04111F;
      font-weight: bold;
    }

    &:disabled { color: rgba(255, 255, 255, 0.25); }
  }

  .el-pagination__total,
  .el-pagination__sizes .el-input .el-input__inner {
    color: rgba(255, 255, 255, 0.7);
    background: rgba(2, 6, 23, 0.6);
    border-color: rgba(114, 149, 207, 0.32);
  }

  .el-select .el-input .el-input__inner {
    color: rgba(255, 255, 255, 0.8);
    background: rgba(2, 6, 23, 0.6);
    border-color: rgba(114, 149, 207, 0.32);
  }
}

// 下拉面板是挂到 body 上的，scoped 样式够不着
/deep/ .el-select-dropdown {
  background: #0d2340;
  border-color: rgba(114, 149, 207, 0.35);

  .el-select-dropdown__item {
    color: rgba(255, 255, 255, 0.78);

    &.hover,
    &:hover {
      background: rgba(24, 144, 255, 0.2);
    }

    &.selected {
      color: #40A9FF;
      font-weight: bold;
    }
  }

  .popper__arrow,
  .popper__arrow::after {
    border-bottom-color: #0d2340 !important;
    border-top-color: #0d2340 !important;
  }
}
</style>
